/**
 * Snapshot dei dati mostrati dai widget nativi sulla home del telefono.
 *
 * Il pacchetto è volutamente piccolo e serializzabile: viene scritto in uno
 * spazio condiviso (App Group su iOS, SharedPreferences su Android) e letto
 * dai widget anche ad app chiusa. Qui solo funzioni pure e testabili.
 */

export type WidgetAgendaItem = {
  /** Titolo mostrato nella riga dell'agenda. */
  title: string;
  /** Materia (se disponibile). */
  subject: string | null;
  /** Tipo: verifica, compito, impegno… */
  kind: string | null;
  /** ISO 8601 UTC. */
  startsAt: string;
};

export type WidgetTimerState = {
  running: boolean;
  /** Modalità del timer (pomodoro, stopwatch…). */
  mode: string;
  /** Secondi già trascorsi nella sessione corrente. */
  elapsedSec: number;
  /** Secondi mancanti per le modalità a conto alla rovescia (null se aperte). */
  remainingSec: number | null;
  /** Fine prevista in ISO 8601, se calcolabile. */
  endsAt: string | null;
};

export type WidgetSnapshot = {
  version: 1;
  /** Lingua dell'interfaccia, così i widget usano gli stessi testi. */
  language: string;
  streak: number;
  streakAlive: boolean;
  xp: number;
  level: number;
  quote: string;
  timer: WidgetTimerState;
  agenda: WidgetAgendaItem[];
  /** Avvisi brevi (compiti in scadenza, classe, ecc.). */
  alerts: string[];
  /** Moduli attivi scelti dall'utente (reminder, timer, progress, agenda). */
  modules: string[];
  /** Profilo dell'account da cui provengono i dati. */
  sourceRole: string;
  updatedAt: string;
};

/** Dimensione massima del JSON condiviso: oltre, i widget vengono troncati. */
export const MAX_SNAPSHOT_BYTES = 8 * 1024;

export const EMPTY_TIMER: WidgetTimerState = {
  running: false,
  mode: "pomodoro",
  elapsedSec: 0,
  remainingSec: null,
  endsAt: null,
};

export function emptySnapshot(now: Date = new Date()): WidgetSnapshot {
  return {
    version: 1,
    language: "it",
    streak: 0,
    streakAlive: false,
    xp: 0,
    level: 1,
    quote: "",
    timer: EMPTY_TIMER,
    agenda: [],
    alerts: [],
    modules: ["reminder", "timer", "progress"],
    sourceRole: "student",
    updatedAt: now.toISOString(),
  };
}

function clampText(v: unknown, max: number): string {
  return typeof v === "string" ? v.trim().slice(0, max) : "";
}

function toIso(value: unknown): string | null {
  if (typeof value !== "string" && !(value instanceof Date)) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export type SnapshotInput = {
  language?: string;
  profile?: { streak_days?: number | null; total_xp?: number | null; level?: number | null } | null;
  streakAlive?: boolean;
  quote?: string;
  timer?: Partial<WidgetTimerState> | null;
  events?: { title?: string | null; subject_name?: string | null; event_type?: string | null; starts_at?: string | null }[];
  alerts?: string[];
  modules?: string[];
  sourceRole?: string;
  agendaLimit?: number;
  now?: Date;
};

/** Costruisce lo snapshot a partire dai dati grezzi dell'app. */
export function buildSnapshot(input: SnapshotInput): WidgetSnapshot {
  const now = input.now ?? new Date();
  const limit = Math.max(1, Math.min(input.agendaLimit ?? 3, 5));

  const agenda: WidgetAgendaItem[] = (input.events ?? [])
    .map((e) => {
      const startsAt = toIso(e.starts_at);
      if (!startsAt) return null;
      return {
        title: clampText(e.title, 60) || clampText(e.subject_name, 60) || "—",
        subject: clampText(e.subject_name, 40) || null,
        kind: clampText(e.event_type, 24) || null,
        startsAt,
      } satisfies WidgetAgendaItem;
    })
    .filter((x): x is WidgetAgendaItem => x !== null)
    .filter((x) => new Date(x.startsAt).getTime() >= now.getTime() - 60 * 60 * 1000)
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
    .slice(0, limit);

  const t = input.timer ?? {};
  const timer: WidgetTimerState = {
    running: Boolean(t.running),
    mode: clampText(t.mode, 20) || "pomodoro",
    elapsedSec: Math.max(0, Math.round(Number(t.elapsedSec ?? 0)) || 0),
    remainingSec:
      typeof t.remainingSec === "number" && Number.isFinite(t.remainingSec)
        ? Math.max(0, Math.round(t.remainingSec))
        : null,
    endsAt: toIso(t.endsAt ?? null),
  };
  if (!timer.endsAt && timer.running && timer.remainingSec !== null) {
    timer.endsAt = new Date(now.getTime() + timer.remainingSec * 1000).toISOString();
  }

  return {
    version: 1,
    language: clampText(input.language, 8) || "it",
    streak: Math.max(0, Math.round(Number(input.profile?.streak_days ?? 0)) || 0),
    streakAlive: input.streakAlive ?? Boolean(input.profile?.streak_days),
    xp: Math.max(0, Math.round(Number(input.profile?.total_xp ?? 0)) || 0),
    level: Math.max(1, Math.round(Number(input.profile?.level ?? 1)) || 1),
    quote: clampText(input.quote, 160),
    timer,
    agenda,
    alerts: (input.alerts ?? []).map((a) => clampText(a, 90)).filter(Boolean).slice(0, 3),
    modules: (input.modules ?? ["reminder", "timer", "progress"])
      .map((m) => clampText(m, 16))
      .filter(Boolean)
      .slice(0, 6),
    sourceRole: clampText(input.sourceRole, 12) || "student",
    updatedAt: now.toISOString(),
  };
}

/** JSON pronto per lo spazio condiviso, troncato se troppo grande. */
export function serializeSnapshot(snapshot: WidgetSnapshot): string {
  let out = JSON.stringify(snapshot);
  if (byteLength(out) <= MAX_SNAPSHOT_BYTES) return out;
  const trimmed: WidgetSnapshot = { ...snapshot, alerts: [], agenda: snapshot.agenda.slice(0, 2) };
  out = JSON.stringify(trimmed);
  if (byteLength(out) <= MAX_SNAPSHOT_BYTES) return out;
  return JSON.stringify({ ...trimmed, agenda: trimmed.agenda.slice(0, 1), quote: "" });
}

export function byteLength(s: string): number {
  return typeof TextEncoder !== "undefined" ? new TextEncoder().encode(s).length : s.length;
}

/** Due snapshot equivalenti (ignorando l'orario di aggiornamento) non vanno riscritti. */
export function snapshotsEqual(a: WidgetSnapshot | null, b: WidgetSnapshot | null): boolean {
  if (!a || !b) return a === b;
  const strip = (s: WidgetSnapshot) => JSON.stringify({ ...s, updatedAt: "" });
  return strip(a) === strip(b);
}

/** Lettura difensiva di uno snapshot salvato (può arrivare da una versione vecchia). */
export function parseSnapshot(raw: unknown): WidgetSnapshot | null {
  if (typeof raw !== "string" || !raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<WidgetSnapshot>;
    if (!parsed || parsed.version !== 1) return null;
    return buildSnapshot({
      language: parsed.language,
      profile: { streak_days: parsed.streak, total_xp: parsed.xp, level: parsed.level },
      streakAlive: parsed.streakAlive,
      quote: parsed.quote,
      timer: parsed.timer ?? null,
      events: (parsed.agenda ?? []).map((a) => ({
        title: a.title,
        subject_name: a.subject,
        event_type: a.kind,
        starts_at: a.startsAt,
      })),
      alerts: parsed.alerts ?? [],
      modules: parsed.modules ?? undefined,
      sourceRole: parsed.sourceRole ?? undefined,
      now: parsed.updatedAt ? new Date(parsed.updatedAt) : new Date(),
    });
  } catch {
    return null;
  }
}
